import { useEffect, useState, useRef } from "react";
import { X, Sparkles, Send, ChevronDown } from "lucide-react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

const DISMISS_KEY = "vk_exit_intent_dismissed";
const SUBMITTED_KEY = "vk_exit_intent_submitted";

export default function ExitIntentPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ student_name: "", phone: "", student_class: "" });
  const armedRef = useRef(true);

  const shouldSkip = () => {
    if (typeof window === "undefined") return true;
    if (pathname.startsWith("/admin") || pathname === "/contact") return true;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return true;
    if (localStorage.getItem(SUBMITTED_KEY) === "1") return true;
    return false;
  };

  useEffect(() => {
    if (shouldSkip()) return;
    let mobileTimer;

    const trigger = () => {
      if (!armedRef.current) return;
      armedRef.current = false;
      setOpen(true);
    };

    const onMouseLeave = (e) => {
      // Desktop: fire when cursor leaves through the top edge
      if (e.clientY <= 0) trigger();
    };

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    if (isMobile) {
      // Mobile fallback: after 25s of activity, if user has scrolled at least 40%, fire once
      mobileTimer = setTimeout(() => {
        const scrolled = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
        if (scrolled > 0.4) trigger();
      }, 25000);
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
    }

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      if (mobileTimer) clearTimeout(mobileTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const close = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.student_name || !form.phone || !form.student_class) {
      toast.error("Please fill all fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", {
        student_name: form.student_name,
        parent_name: form.student_name,
        student_class: form.student_class,
        board: "CBSE",
        subjects: "Free demo request (exit intent)",
        phone: form.phone,
        heard_from: "Website popup",
        message: "Lead captured via exit-intent popup",
      });
      localStorage.setItem(SUBMITTED_KEY, "1");
      toast.success("Got it!", { description: "We'll call you within 24 hours to set up your free demo." });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="exit-intent-popup"
      className="fixed inset-0 z-[70] grid place-items-center bg-black/60 backdrop-blur-sm px-4 animate-in fade-in duration-200"
      onClick={close}
    >
      <div
        className="relative max-w-lg w-full rounded-lg overflow-hidden bg-white shadow-2xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          data-testid="exit-popup-close"
          aria-label="Close popup"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 text-white hover:bg-white/20 grid place-items-center z-10"
        >
          <X size={16} />
        </button>

        <div className="bg-[#0B192C] text-white px-8 pt-8 pb-6 relative overflow-hidden">
          <div className="vk-grain" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 vk-chip"><Sparkles size={12} /> Wait — before you go</div>
            <h3 className="mt-4 font-display text-3xl font-800 leading-tight">
              Book a <span className="text-[#FF6B35]">free demo class</span> in 30 seconds
            </h3>
            <p className="mt-2 text-sm text-white/70">Leave your details — our team will call you back within 24 hours to set it up. No entrance test, no commitment.</p>
          </div>
        </div>

        <form onSubmit={submit} className="p-8 space-y-4">
          <div>
            <Label htmlFor="ei-name">Student Name</Label>
            <Input id="ei-name" data-testid="exit-popup-name" value={form.student_name} onChange={(e) => setForm(f => ({ ...f, student_name: e.target.value }))} required className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ei-phone">Phone</Label>
              <Input id="ei-phone" data-testid="exit-popup-phone" value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} required className="mt-1" />
            </div>
            <div>
              <Label>Class</Label>
              <div className="relative mt-1">
                <select
                  data-testid="exit-popup-class"
                  value={form.student_class}
                  onChange={(e) => setForm(f => ({ ...f, student_class: e.target.value }))}
                  required
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm appearance-none pr-9 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B35]"
                >
                  <option value="" disabled>Select</option>
                  {[
                    { v: "VI", l: "Class VI" },
                    { v: "VII", l: "Class VII" },
                    { v: "VIII", l: "Class VIII" },
                    { v: "IX", l: "Class IX" },
                    { v: "X", l: "Class X" },
                  ].map(o => (<option key={o.v} value={o.v}>{o.l}</option>))}
                </select>
                <ChevronDown size={16} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-500" />
              </div>
            </div>
          </div>
          <button type="submit" disabled={submitting} data-testid="exit-popup-submit" className="vk-btn-primary w-full justify-center">
            {submitting ? "Sending…" : (<>Yes, book my free demo <Send size={16} /></>)}
          </button>
          <button type="button" onClick={close} className="w-full text-xs text-slate-500 hover:text-slate-700 transition-colors">No thanks, I&apos;ll keep browsing</button>
        </form>
      </div>
    </div>
  );
}
